import { useTranslation } from 'react-i18next';
import SingleSelect from '../SingleSelect';
import Input from '../Input';
import Checkmark from '../Checkmark';
import MultiSelect from '../MultiSelect';
import './style.css';
import { useState } from 'react';
import { Button, message } from 'antd';
import PhotoCapture from './components/PhotoCapture';
import { deleteFile, saveFile } from '../../../Maintenance/helpers/files';
import { useMsal } from '@azure/msal-react';
import { useParams, useLocation } from 'react-router-dom';
import useTemplateState from '../../atoms/variables';
import Radio from '../Radio';

const WebForm = ({ data, onSubmit, onBack }) => {
  const { instance } = useMsal();
  const { setPendingApiCalls } = useTemplateState();
  const { assetId, interconnectionId } = useParams();
  const location = useLocation();

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [formData, setFormData] = useState(data);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldChange = (stepIndex, fieldIndex, event) => {
    const value = event.target.value;

    setFormData((prevData) => {
      const newData = { ...prevData };
      const currentField = newData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex];

      if (currentField.type === 'select' || currentField.type === 'radio') {
        handleSelectUpdate(fieldIndex, stepIndex, value);
      } else {
        // Para outros tipos de campo, atualiza o valor diretamente em todas as línguas
        ['pt', 'en', 'es'].forEach((lang) => {
          if (newData[lang]?.plan?.steps?.[stepIndex]?.fields?.[fieldIndex]) {
            const field = newData[lang].plan.steps[stepIndex].fields[fieldIndex];
            field.userGivenValue = value;
          }
        });
      }

      return newData;
    });
  };

  const handleSelectUpdate = (fieldIndex, stepIndex, value) => {
    const currentField = formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex];

    if (currentField.variant === 'multiple') {
      // Para multiselect, mantém o valor como array
      ['pt', 'en', 'es'].forEach((lang) => {
        if (formData[lang]?.plan?.steps?.[stepIndex]?.fields?.[fieldIndex]) {
          const field = formData[lang].plan.steps[stepIndex].fields[fieldIndex];
          field.userGivenValue = value.map((v) => ({
            id: field.options[currentField.options.findIndex((option) => option === v)],
          }));
        }
      });
    } else {
      // Para select normal, encontra o índice da opção selecionada na língua atual
      const selectedIndex = currentField.options.findIndex((option) => option === value);

      // Atualiza o valor em todas as línguas usando o mesmo índice
      ['pt', 'en', 'es'].forEach((lang) => {
        if (formData[lang]?.plan?.steps?.[stepIndex]?.fields?.[fieldIndex]) {
          const field = formData[lang].plan.steps[stepIndex].fields[fieldIndex];
          field.userGivenValue = [field.options[selectedIndex]];
        }
      });
    }
  };

  const renderField = (field, stepIndex, fieldIndex) => {
    const commonProps = {
      givenKey: `${stepIndex}-${fieldIndex}-${currentLanguage}`,
      name: field.label,
      required: field.required,
      onChange: (event) => handleFieldChange(stepIndex, fieldIndex, event),
    };

    switch (field.type) {
      case 'select':
        if (field.variant === 'multiple') {
          return (
            <MultiSelect
              {...commonProps}
              value={
                formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex].userGivenValue
              }
              options={field.options?.map((option) => ({ id: option, label: option }))}
            />
          );
        }
        return (
          <SingleSelect
            {...commonProps}
            value={{
              id:
                formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex]
                  .userGivenValue?.[0] ?? '',
            }}
            options={field.options?.map((option) => ({ id: option, label: option }))}
          />
        );

      case 'textInput':
        return (
          <Input
            {...commonProps}
            value={
              formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex].userGivenValue
            }
            type={field.variant || 'text'}
          />
        );

      case 'checkbox':
        return (
          <Checkmark
            {...commonProps}
            checked={
              formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex].userGivenValue ===
              true
            }
            label={field.variant}
          />
        );

      case 'radio':
        return (
          <Radio
            {...commonProps}
            value={{
              id:
                formData[currentLanguage].plan.steps[stepIndex].fields[fieldIndex]
                  .userGivenValue?.[0] ?? '',
            }}
            options={field.options?.map((option) => ({ id: option, label: option }))}
          />
        );

      default:
        return null;
    }
  };

  const checkForMissingFields = () => {
    const steps = formData[currentLanguage].plan.steps;
    const missingFields = [];

    steps.forEach((step, stepIndex) => {
      step.fields.forEach((field, fieldIndex) => {
        if (field.type === 'checkbox' && !field.userGivenValue) {
          // Se for checkbox sem valor definido, inicializa com false
          handleFieldChange(stepIndex, fieldIndex, {
            target: { value: false },
          });
        }

        if (
          field.required &&
          (field.userGivenValue === undefined ||
            field.userGivenValue === null ||
            field.userGivenValue === '')
        ) {
          missingFields.push(`${step.title} - ${field.label}`);
        }
      });
    });

    return missingFields;
  };

  // Helper function to transform array objects into strings
  const cleanArrayObjects = (data) => {
    const transformedData = { ...data };

    ['pt', 'en', 'es'].forEach((lang) => {
      if (transformedData[lang]?.plan?.steps) {
        transformedData[lang].plan.steps.forEach((step) => {
          step.fields.forEach((field) => {
            if (Array.isArray(field.userGivenValue)) {
              field.userGivenValue = field.userGivenValue.map((item) => {
                if (typeof item === 'object' && item !== null) {
                  return item.id;
                }
                return item;
              });
            }
          });
        });
      }
    });

    return transformedData;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const missingFields = checkForMissingFields();

      if (missingFields.length > 0) {
        message.error(t('Please fill in all required fields'));
        return;
      }

      const transformedData = cleanArrayObjects(formData);

      if (onSubmit) {
        await onSubmit(transformedData, currentLanguage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const photoCaptureHandler = async (stepIndex, subStepIndex, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return; // Exit if no file was selected
    try {
      setPendingApiCalls((current) => current.concat(['saveFile']));
      const file = files[0];
      const response = await saveFile(
        instance,
        location.pathname.includes('assets') ? 'asset' : 'interconnection',
        assetId ?? interconnectionId,
        file,
      );
      // Set the imagePath in the sub-step
      const tmp = { ...formData };
      ['pt', 'en', 'es'].forEach((lang) => {
        tmp[lang].plan.steps[stepIndex].steps[subStepIndex].imagePath = response.blobPath;
      });
      setFormData(tmp);

      return response;
    } catch (error) {
      message.error(t('Error saving file'));
    } finally {
      setPendingApiCalls((current) => current.filter((call) => call !== 'saveFile'));
    }
  };

  const deleteFileHandler = async (stepIndex, subStepIndex, fileName) => {
    console.log('Delete file:', fileName);
    try {
      setPendingApiCalls((current) => current.concat(['deleteFile']));
      const response = await deleteFile(
        instance,
        location.pathname.includes('assets') ? 'asset' : 'interconnection',
        assetId ?? interconnectionId,
        fileName,
      );

      // Remove the imagePath from the sub-step
      const tmp = { ...formData };
      ['pt', 'en', 'es'].forEach((lang) => {
        delete tmp[lang].plan.steps[stepIndex].steps[subStepIndex].imagePath;
      });
      setFormData(tmp);

      return response;
    } catch (error) {
      message.error(t('Error deleting file'));
    } finally {
      setPendingApiCalls((current) => current.filter((call) => call !== 'deleteFile'));
    }
  };

  const createNewCameraSubStep = (stepIndex, title) => {
    // Update the formData state to trigger a re-render
    setFormData((prevData) => {
      const updatedData = { ...prevData };
      ['pt', 'en', 'es'].forEach((lang) => {
        const cameraStep = updatedData[lang].plan.steps[stepIndex];
        cameraStep.steps.push({
          title: title,
          description: {
            text: '',
            hints: [''],
          },
        });
      });
      return updatedData;
    });
  };

  return (
    <div className='webFormContainer'>
      <h1 className='webFormTitle'>{formData?.[currentLanguage]?.title}</h1>
      {formData?.[currentLanguage]?.plan?.steps.map((step, stepIndex) => (
        <div className='stepContainer' key={stepIndex}>
          <h2 className='sticky'>{`${stepIndex + 1}. ${step.title}`}</h2>

          {step.fields.map((field, fieldIndex) => (
            <div key={fieldIndex} className='formsInputContainer'>
              <div className='formsInputLabel'>
                {field.label}
                {field.required && <span className='requiredMark'>*</span>}
              </div>
              <div className='formsInput'>{renderField(field, stepIndex, fieldIndex)}</div>
            </div>
          ))}

          {step.type === 'camera' ||
            (step.type === 'evidency' && (
              <PhotoCapture
                id={`photo-capture-${stepIndex}`}
                steps={step.steps}
                photoCaptureHandler={(subStepIndex, event) =>
                  photoCaptureHandler(stepIndex, subStepIndex, event)
                }
                deleteFileHandler={(subStepIndex, fileName) =>
                  deleteFileHandler(stepIndex, subStepIndex, fileName)
                }
                createNewCameraSubStep={(title) => createNewCameraSubStep(stepIndex, title)}
              />
            ))}
        </div>
      ))}
      <div className='formActions'>
        <Button
          className='back-button'
          size='large'
          style={{
            borderColor: '#333333',
            color: '#333333',
            background: 'white',
          }}
          onClick={onBack}
        >
          {t('back')}
        </Button>
        <Button
          size='large'
          style={{
            borderColor: 'white',
            background: '#1777FF',
            color: 'white',
          }}
          onClick={handleSubmit}
          loading={isSubmitting}
        >
          {t('save_form')}
        </Button>
      </div>
    </div>
  );
};

export default WebForm;
